import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserRole } from '@bhops/shared';
import { optionGroupsApi } from '@/api/option-groups';
import { extractApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { useAuthStore } from '@/stores/auth';

// Plan v0.3.0 §G-3 (PPTX Slide 7+8) — operator-editable picklist
// component. Wraps a native <select> with:
//
//   - data fetched from `/option-groups/:groupKey/values`
//   - "옵션 추가/삭제…" sentinel option (manager+ only) → opens combined
//     add-form + soft-archive panel (windows-ux-edit Round 2 Phase E #4).
//
// FB2 §4A/5A — Manager+ can soft-archive options. Historical labels still
// resolve, so there is no usage-count check and the currently-selected value
// is left untouched even if it gets archived.
//
// windows-ux-edit Round 2 Phase E (#4) — "새 옵션 추가…" sentinel + 별도
// 기어(⚙) 버튼 두 진입점을 "옵션 추가/삭제…" 하나로 통합. 선택 시 추가 폼 +
// 기존 옵션 보관 목록이 같은 팝오버 안에 표시된다.
//
// Distinguished from `<Select>` (used for enum-locked fields) by the
// fact that operators can extend the picklist without a code deploy.

interface Props {
  /** OptionGroup key (e.g. "robot.color", "robot.loaderType"). */
  groupKey: string;
  /** Currently-selected value (or empty string for "no selection"). */
  value: string;
  onChange: (value: string) => void;
  /** Optional placeholder for the "no selection" entry. */
  placeholder?: string;
  /** Pin disabled state from the parent (form busy etc.). */
  disabled?: boolean;
  /** Custom inline style for the <select>. */
  style?: CSSProperties;
  /** Optional name for form serialization. */
  name?: string;
  /** ID for label[for] association. */
  id?: string;
}

const ADD_ROLES: ReadonlyArray<UserRole> = [
  UserRole.ADMIN,
  UserRole.MANAGER,
  UserRole.SALES_MANAGER,
  UserRole.MFG_MANAGER,
  UserRole.FIELD_MANAGER,
];

export function EditableSelect({
  groupKey,
  value,
  onChange,
  placeholder,
  disabled = false,
  style,
  name,
  id,
}: Props) {
  const role = useAuthStore((s) => s.user?.role);
  const canAddOptions = !!role && ADD_ROLES.includes(role);

  const queryClient = useQueryClient();
  const valuesQ = useQuery({
    queryKey: ['option-groups', groupKey],
    queryFn: () => optionGroupsApi.listValues(groupKey),
    staleTime: 5 * 60_000,
  });

  const [draftValue, setDraftValue] = useState('');
  const [confirmArchiveId, setConfirmArchiveId] = useState<string | null>(null);
  // windows-ux-edit Round 2 Phase E — 추가 폼 + 보관 목록을 하나의 패널로.
  // `managing` 하나로 패널 열림/닫힘을 제어한다(이전의 `adding` state 통합).
  const [managing, setManaging] = useState(false);

  function closeManaging(): void {
    setManaging(false);
    setConfirmArchiveId(null);
    setConfirmHardDeleteId(null);
    setShowArchived(false);
    setDraftValue('');
  }

  // 패널 바깥 클릭 시 닫기(추가 폼 포함).
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!managing) return;
    function onDocMouseDown(e: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        closeManaging();
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [managing]);

  const addM = useMutation({
    mutationFn: () =>
      optionGroupsApi.addValue(groupKey, {
        value: draftValue.trim(),
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['option-groups', groupKey] });
      onChange(created.value);
      closeManaging();
    },
  });

  const archiveM = useMutation({
    mutationFn: (valueId: string) => optionGroupsApi.archiveValue(valueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['option-groups', groupKey] });
      setConfirmArchiveId(null);
    },
  });

  // 이슈1d — 보관(archive)된 값의 복원/완전삭제. 기존엔 추가+보관만 가능하고
  // 보관된 값을 인라인에서 되살리거나 영구 삭제할 길이 없어, 운영자가 admin
  // 페이지(고객유형은 거기에도 미등록)로 가야만 했다. 같은 팝오버에서 처리한다.
  const [showArchived, setShowArchived] = useState(false);
  const [confirmHardDeleteId, setConfirmHardDeleteId] = useState<string | null>(null);

  // includeArchived=true 는 활성+보관 전체를 주므로 archived 만 추린다. 패널을
  // 열고 '보관된 값 보기'를 켰을 때만 fetch. 같은 ['option-groups', groupKey]
  // prefix 라 add/archive/restore 의 invalidate 가 이 쿼리도 갱신한다.
  const archivedQ = useQuery({
    queryKey: ['option-groups', groupKey, 'all'],
    queryFn: () => optionGroupsApi.listValues(groupKey, true),
    enabled: managing && showArchived,
    staleTime: 5 * 60_000,
  });
  const archivedItems = (archivedQ.data ?? []).filter((o) => o.archived);

  const restoreM = useMutation({
    mutationFn: (valueId: string) => optionGroupsApi.restoreValue(valueId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['option-groups', groupKey] }),
  });
  const hardDeleteM = useMutation({
    mutationFn: (valueId: string) => optionGroupsApi.hardDeleteValue(valueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['option-groups', groupKey] });
      setConfirmHardDeleteId(null);
    },
  });

  const items = valuesQ.data ?? [];

  // windows-ux-edit Round 2 Phase E — 기어 버튼을 제거하고 sentinel 하나로 통합.
  // sentinel 선택 시 managing 패널 열기(추가 폼 + 보관 목록 동시 표시).
  const ADD_SENTINEL = '__add__';

  function handleSelectChange(next: string) {
    if (next === ADD_SENTINEL) {
      setManaging(true);
      return;
    }
    onChange(next);
  }

  // sentinel 노출 조건: 매니저+ + disabled 아닐 때.
  const canManage = canAddOptions && !disabled;

  return (
    <div ref={wrapRef} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ position: 'relative' }}>
        <select
          name={name}
          id={id}
          value={value}
          onChange={(e) => handleSelectChange(e.target.value)}
          disabled={disabled || valuesQ.isLoading}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            flex: '1 1 auto',
            minWidth: 0,
            padding: '6px 8px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 13,
            background: 'var(--bg-surface)',
            color: 'var(--fg-default)',
            ...style,
          }}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {items.map((opt) => (
            <option key={opt.id} value={opt.value}>
              {opt.label ?? opt.value}
            </option>
          ))}
          {/* windows-ux-edit Round 2 Phase E (#4) — 단일 진입점으로 통합. */}
          {canManage && <option value={ADD_SENTINEL}>옵션 추가/삭제…</option>}
        </select>

        {/* 통합 패널: 추가 폼 + 보관 목록 */}
        {managing && canManage && (
          <div style={managePopover} role="dialog" aria-label="옵션 추가/삭제">
            <div style={manageHeader}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-default)' }}>
                옵션 추가/삭제
              </span>
              <button
                type="button"
                onClick={closeManaging}
                style={manageCloseBtn}
                aria-label="닫기"
              >
                <Icon name="x" size={12} aria-hidden />
              </button>
            </div>

            {/* 추가 폼 */}
            <div style={addRow}>
              <input
                placeholder="예: Black"
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && draftValue.trim() && !addM.isPending) addM.mutate();
                }}
                style={addInput}
                autoFocus
                aria-label="새 옵션 값"
              />
              <button
                type="button"
                onClick={() => addM.mutate()}
                disabled={!draftValue.trim() || addM.isPending}
                style={addBtn}
              >
                {addM.isPending ? '저장 중…' : '추가'}
              </button>
            </div>
            {addM.isError && <div style={errorText}>{extractApiError(addM.error)}</div>}

            {/* 기존 옵션 목록 + 보관 */}
            {items.length > 0 && (
              <>
                <ul style={manageList}>
                  {items.map((opt) => (
                    <li key={opt.id} style={manageRow}>
                      <span style={manageLabel}>{opt.label ?? opt.value}</span>
                      {confirmArchiveId === opt.id ? (
                        <span style={manageConfirm} role="alertdialog" aria-label="옵션 보관 확인">
                          <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                            보관할까요?
                          </span>
                          <button
                            type="button"
                            onClick={() => archiveM.mutate(opt.id)}
                            disabled={archiveM.isPending}
                            style={confirmDeleteBtn}
                          >
                            {archiveM.isPending ? '보관 중…' : '보관'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmArchiveId(null)}
                            disabled={archiveM.isPending}
                            style={cancelBtn}
                          >
                            취소
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmArchiveId(opt.id)}
                          disabled={archiveM.isPending}
                          style={manageDeleteBtn}
                          title={`${opt.label ?? opt.value} 옵션 보관`}
                          aria-label={`${opt.label ?? opt.value} 옵션 보관`}
                        >
                          <Icon name="x" size={12} aria-hidden />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                <div style={manageHint}>보관해도 기존 데이터의 라벨은 유지됩니다.</div>
              </>
            )}

            {archiveM.isError && <div style={errorText}>{extractApiError(archiveM.error)}</div>}

            {/* 이슈1d — 보관된 값 복원/완전삭제 섹션 */}
            <div style={archivedSection}>
              <button
                type="button"
                onClick={() => setShowArchived((v) => !v)}
                style={archivedToggle}
                aria-expanded={showArchived}
              >
                <Icon name={showArchived ? 'chev-down' : 'chev-right'} size={12} aria-hidden />
                {showArchived ? '보관된 값 숨기기' : '보관된 값 보기'}
              </button>
              {showArchived &&
                (archivedQ.isLoading ? (
                  <div style={manageHint}>불러오는 중…</div>
                ) : archivedItems.length === 0 ? (
                  <div style={manageHint}>보관된 값이 없습니다.</div>
                ) : (
                  <ul style={manageList}>
                    {archivedItems.map((opt) => (
                      <li key={opt.id} style={manageRow}>
                        <span style={archivedLabel}>{opt.label ?? opt.value}</span>
                        {confirmHardDeleteId === opt.id ? (
                          <span
                            style={manageConfirm}
                            role="alertdialog"
                            aria-label="옵션 완전 삭제 확인"
                          >
                            <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                              완전히 삭제할까요?
                            </span>
                            <button
                              type="button"
                              onClick={() => hardDeleteM.mutate(opt.id)}
                              disabled={hardDeleteM.isPending}
                              style={confirmDeleteBtn}
                            >
                              {hardDeleteM.isPending ? '삭제 중…' : '삭제'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmHardDeleteId(null)}
                              disabled={hardDeleteM.isPending}
                              style={cancelBtn}
                            >
                              취소
                            </button>
                          </span>
                        ) : (
                          <span style={manageConfirm}>
                            <button
                              type="button"
                              onClick={() => restoreM.mutate(opt.id)}
                              disabled={restoreM.isPending}
                              style={restoreBtn}
                              title={`${opt.label ?? opt.value} 복원`}
                            >
                              {restoreM.isPending ? '복원 중…' : '복원'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmHardDeleteId(opt.id)}
                              disabled={hardDeleteM.isPending}
                              style={manageDeleteBtn}
                              title={`${opt.label ?? opt.value} 완전 삭제`}
                              aria-label={`${opt.label ?? opt.value} 완전 삭제`}
                            >
                              완전 삭제
                            </button>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ))}
              {(restoreM.isError || hardDeleteM.isError) && (
                <div style={errorText}>{extractApiError(restoreM.error ?? hardDeleteM.error)}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const addRow: CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
};

const addInput: CSSProperties = {
  padding: '4px 8px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  flex: '1 1 100px',
  minWidth: 0,
};

const addBtn: CSSProperties = {
  padding: '4px 10px',
  background: 'var(--blue-600)',
  // round5 #21 — 채도 배경 위 텍스트는 brand-on(라이트/다크 공통 흰색).
  // fg-inverse 는 다크에서 어두운 색으로 뒤집혀 부적합.
  color: 'var(--brand-on)',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  cursor: 'pointer',
  flexShrink: 0,
};

const cancelBtn: CSSProperties = {
  padding: '4px 10px',
  background: 'transparent',
  color: 'var(--fg-muted)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  cursor: 'pointer',
};

const errorText: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-danger)',
  marginTop: 2,
};

const managePopover: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  right: 0,
  zIndex: 20,
  minWidth: 220,
  maxWidth: 300,
  maxHeight: 320,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 10,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  boxShadow: 'var(--shadow-md)',
};

const manageHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const manageCloseBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  border: 'none',
  background: 'transparent',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: 1,
  padding: '2px 4px',
  borderRadius: 'var(--radius-sm)',
};

const manageList: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const manageRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '3px 4px',
  borderRadius: 'var(--radius-sm)',
};

const manageLabel: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-default)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: '1 1 auto',
  minWidth: 0,
};

const manageDeleteBtn: CSSProperties = {
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  border: 'none',
  background: 'transparent',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: 1,
  padding: '2px 6px',
  borderRadius: 'var(--radius-sm)',
};

const manageConfirm: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  flex: '0 0 auto',
};

const manageHint: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-muted)',
  borderTop: '1px solid var(--border-subtle, var(--border-default))',
  paddingTop: 6,
};

const confirmDeleteBtn: CSSProperties = {
  padding: '4px 10px',
  background: 'var(--fg-danger)',
  color: 'var(--brand-on)',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  cursor: 'pointer',
};

const archivedSection: CSSProperties = {
  borderTop: '1px solid var(--border-subtle, var(--border-default))',
  paddingTop: 6,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const archivedToggle: CSSProperties = {
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  border: 'none',
  background: 'transparent',
  color: 'var(--fg-muted)',
  cursor: 'pointer',
  fontSize: 11,
  padding: '2px 2px',
};

const archivedLabel: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-muted)',
  textDecoration: 'line-through',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: '1 1 auto',
  minWidth: 0,
};

const restoreBtn: CSSProperties = {
  padding: '4px 10px',
  background: 'transparent',
  color: 'var(--blue-600)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  cursor: 'pointer',
};
